import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GuideSlugIcon } from '../components/guides/guide-icons';
import { ChevronIcon } from '../components/icons';
import { useGuidesQuery } from '../hooks/use-guides-query';
import { useAuth } from '../hooks/use-auth';
import { GUIDE_CATEGORIES } from '../config/reference-data';
import type { ExpatGuideTopic } from '../types/expat-guides';

interface GuidesProps {
  readonly onSignUp?: () => void;
}

export function Guides({ onSignUp }: GuidesProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [filter, setFilter] = useState<(typeof GUIDE_CATEGORIES)[number]>('All');

  const guides = useGuidesQuery({ enabled: !!user });

  const visible = useMemo(() => {
    const all = guides.data ?? [];
    if (filter === 'All') return all;
    const slug = filter.toLowerCase();
    return all.filter(
      (t) =>
        t.category.toLowerCase() === slug ||
        // Some categories cluster across multiple slugs; keep the simple
        // filter forgiving so e.g. "Healthcare" matches the 'healthcare'
        // category and won't accidentally hide adjacent rows.
        t.slug.toLowerCase().includes(slug),
    );
  }, [guides.data, filter]);

  return (
    <div className="nw-app nw-app--two">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.guides.sidebar.categories')}</div>
        {GUIDE_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            className={`nw-side__item ${filter === c ? 'is-active' : ''}`}
            onClick={() => setFilter(c)}
          >
            <span className="nw-side__item-label">{c}</span>
          </button>
        ))}
      </aside>

      <section className="nw-feed">
        <div className="nw-feed__head">
          <div>
            <h1 className="nw-feed__title">{t('web.guides.title')}</h1>
            <p className="nw-feed__sub">{t('web.guides.subtitle')}</p>
          </div>
        </div>

        {!user && (
          <div className="nw-feed__empty">
            {t('web.guides.emptyUnauthenticated')}
            {onSignUp && (
              <div style={{ marginTop: 12 }}>
                <button type="button" className="nw-btn nw-btn--primary" onClick={onSignUp}>
                  {t('web.guides.emptyUnauthenticatedCta')}
                </button>
              </div>
            )}
          </div>
        )}

        {user && guides.isLoading && (
          <div className="nw-feed__loading">
            <div className="nw-feed__loading-spinner" aria-hidden />
            {t('web.guides.loading')}
          </div>
        )}

        {user && !guides.isLoading && visible.length === 0 && (
          <div className="nw-feed__empty">{t('web.guides.emptyCategory')}</div>
        )}

        {visible.length > 0 && (
          <div className="nw-guide-grid">
            {visible.map((topic) => (
              <GuideTopicCard
                key={topic.id}
                topic={topic}
                onOpen={() => navigate(`/guides/${topic.id}`)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface GuideTopicCardProps {
  readonly topic: ExpatGuideTopic;
  readonly onOpen: () => void;
}

function GuideTopicCard({ topic, onOpen }: GuideTopicCardProps) {
  const { t } = useTranslation();
  const { content } = topic;
  const accent = content.accent;
  const accentDim = content.accentDim;
  const chapters = content.slides.length;
  const keyStat = content.slides[0]?.facts?.[0];
  const sourceDomain = (() => {
    const u = content.slides[0]?.sources?.[0]?.url;
    if (!u) return null;
    return u.replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '');
  })();

  return (
    <button
      type="button"
      className="nw-guide-card"
      onClick={onOpen}
      style={{
        // accent ribbon at the top, set inline so each topic gets its
        // own colour straight from the API payload.
        ['--guide-accent' as string]: accent,
      }}
    >
      <span className="nw-guide-card__accent" aria-hidden />

      <div className="nw-guide-card__head">
        <span className="nw-guide-card__icon" style={{ color: accent }} aria-hidden>
          <GuideSlugIcon slug={topic.slug} size={22} color={accent} />
        </span>
        <span className="nw-guide-card__chapters">
          {t('web.guides.chapters', { count: chapters })}
        </span>
      </div>

      <h3 className="nw-guide-card__title">{content.title}</h3>
      <p className="nw-guide-card__sub">{content.subtitle}</p>

      {keyStat && (
        <div
          className="nw-guide-card__stat"
          style={{
            background: accentDim,
            borderColor: `${accent}33`,
            color: accent,
          }}
        >
          <span className="nw-guide-card__stat-label">{keyStat.label}</span>
          <span className="nw-guide-card__stat-val">{keyStat.val}</span>
        </div>
      )}

      <div className="nw-guide-card__foot">
        {sourceDomain && <span className="nw-guide-card__domain">{sourceDomain}</span>}
        <span className="nw-guide-card__cat" style={{ color: accent }}>
          {topic.category.charAt(0).toUpperCase() + topic.category.slice(1)}
          <ChevronIcon dir="right" size={12} stroke={2.4} />
        </span>
      </div>
    </button>
  );
}
